import { Stack, Typography } from "@mui/material";
import { first, isEmpty, isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import { pageTabHeaderTypographyProps } from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import FoldableBlock from "@/common/components/Display/FoldableBlock";
import PoolView from "@/common/components/Entity/Pool/View/PoolView";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AppTab from "@/common/components/Tab/AppTab";
import AppTabs from "@/common/components/Tab/AppTabs";
import { ViewVariant } from "@/common/ts/layout";
import {
  AssetSubscriptionPlanDto,
  AssetSubscriptionPlanProductLocationDto,
} from "@/core/api/generated/v1";

import ProductLocationInline from "../../../ProductLocation/ProductLocationInline";

interface Props {
  assetSubscriptionPlan: AssetSubscriptionPlanDto;
}

export default function PoolsTabContent({ assetSubscriptionPlan }: Props) {
  const [selectedPlanProductLocation, setSelectedPlanProductLocation] = useState<
    AssetSubscriptionPlanProductLocationDto | undefined
  >(first(assetSubscriptionPlan.productLocations));

  return (
    <PageTabContent>
      <ViewLayout displayProps={{ viewVariant: ViewVariant.Tab }}>
        <ViewContentBlock>
          <FieldValue
            label='Ensure pool belongs to a single plan'
            isEmpty={isNil(assetSubscriptionPlan?.settings2?.isEnsurePoolBelongToSinglePlan)}
          >
            <BooleanValue value={assetSubscriptionPlan.settings2?.isEnsurePoolBelongToSinglePlan} />
          </FieldValue>

          {/* Main pool */}
          <FoldableBlock
            defaultIsFolded
            trigger={{
              label: (
                <Typography component='span' variant='h6'>
                  Primary pool
                </Typography>
              ),
            }}
          >
            <Stack spacing={2}>
              {!assetSubscriptionPlan.pool?.id && <NoDataAlert />}

              {assetSubscriptionPlan.pool?.id && (
                <PoolView
                  poolId={assetSubscriptionPlan.pool?.id}
                  pool={undefined}
                  displayProps={{
                    breadcrumbs: false,
                    header: true,
                    actions: true,
                  }}
                  headerProps={{
                    withLink: true,
                    typographyProps: pageTabHeaderTypographyProps.title,
                  }}
                  onDelete={null}
                />
              )}
            </Stack>
          </FoldableBlock>

          {/* Secondary pools */}
          <FoldableBlock
            defaultIsFolded
            trigger={{
              label: (
                <Typography component='span' variant='h6'>
                  Secondary pools (per sales location)
                </Typography>
              ),
            }}
          >
            <Stack spacing={2}>
              {isEmpty(assetSubscriptionPlan.productLocations) && <NoDataAlert />}

              {!isEmpty(assetSubscriptionPlan.productLocations) && (
                <>
                  <AppTabs
                    value={selectedPlanProductLocation}
                    onChange={(e, newValue) => setSelectedPlanProductLocation(newValue)}
                  >
                    {assetSubscriptionPlan.productLocations?.map((x, i) => (
                      <AppTab
                        key={i}
                        label={
                          <ProductLocationInline
                            entity={undefined}
                            entityId={x.productLocation?.id}
                            withIcon={false}
                          />
                        }
                        value={x}
                      />
                    ))}
                  </AppTabs>

                  {selectedPlanProductLocation && !selectedPlanProductLocation.pool && (
                    <NoDataAlert />
                  )}

                  {selectedPlanProductLocation && (
                    <PoolView
                      poolId={selectedPlanProductLocation.pool?.id}
                      pool={undefined}
                      displayProps={{
                        breadcrumbs: false,
                        header: true,
                        actions: true,
                      }}
                      headerProps={{
                        withLink: true,
                        typographyProps: pageTabHeaderTypographyProps.title,
                      }}
                      onDelete={null}
                    />
                  )}
                </>
              )}
            </Stack>
          </FoldableBlock>
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
